import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useOnSudoLayoutEvent } from "src/lib/layout-events";

interface Particle {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  startX: number;
  startY: number;
  progress: number;
  speed: number;
  size: number;
  opacity: number;
  arcOffset: number;
}

const STREAM_COUNT = 18;
const ANIMATION_DURATION_MS = 700;

function getComputedColor(varName: string): string {
  return getComputedStyle(document.body).getPropertyValue(varName).trim();
}

function easeInOutQuad(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

let lastPointerX = 0;
let lastPointerY = 0;

function trackPointer(e: PointerEvent | MouseEvent) {
  lastPointerX = e.clientX;
  lastPointerY = e.clientY;
}

export function CTAAttentionOverlay() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    document.addEventListener("pointerdown", trackPointer, true);
    document.addEventListener("mousedown", trackPointer, true);
    return () => {
      document.removeEventListener("pointerdown", trackPointer, true);
      document.removeEventListener("mousedown", trackPointer, true);
    };
  }, []);

  const animate = useCallback(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    canvas.style.display = "block";

    const rightPanel = document.querySelector(".right-panel");
    let targetX = window.innerWidth - 200;
    let targetY = window.innerHeight / 2;
    if (rightPanel) {
      const rect = rightPanel.getBoundingClientRect();
      targetX = rect.left + 40;
      targetY = rect.top + rect.height * 0.35;
    }

    const color = getComputedColor("--primary-cta-high-contrast") || "#5716e3";

    const srcX = lastPointerX || window.innerWidth * 0.35;
    const srcY = lastPointerY || window.innerHeight * 0.5;

    const particles: Particle[] = [];
    for (let i = 0; i < STREAM_COUNT; i++) {
      particles.push({
        x: srcX,
        y: srcY,
        startX: srcX,
        startY: srcY,
        targetX: targetX + (Math.random() - 0.5) * 30,
        targetY: targetY + (Math.random() - 0.5) * 60,
        progress: 0,
        speed: 0.7 + Math.random() * 0.5,
        size: 4 + Math.random() * 3,
        opacity: 0.65 + Math.random() * 0.35,
        arcOffset: (Math.random() - 0.5) * 140,
      });
    }

    const startTime = performance.now();

    const frame = (now: number) => {
      const elapsed = now - startTime;
      const globalProgress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let allDone = true;

      for (const p of particles) {
        p.progress = Math.min(globalProgress * p.speed, 1);
        if (p.progress < 1) allDone = false;
        if (p.progress < 0.01) continue;

        const t = easeInOutQuad(p.progress);

        const midX = (p.startX + p.targetX) / 2 + p.arcOffset;
        const midY = Math.min(p.startY, p.targetY) - 30 - Math.abs(p.arcOffset) * 0.25;

        const oneMinusT = 1 - t;
        p.x = oneMinusT * oneMinusT * p.startX + 2 * oneMinusT * t * midX + t * t * p.targetX;
        p.y = oneMinusT * oneMinusT * p.startY + 2 * oneMinusT * t * midY + t * t * p.targetY;

        const fadeIn = Math.min(p.progress * 5, 1);
        const fadeOut = p.progress > 0.6 ? 1 - (p.progress - 0.6) / 0.4 : 1;
        const alpha = p.opacity * fadeIn * fadeOut;

        const currentSize = p.size * (1 - p.progress * 0.3);

        ctx.beginPath();
        ctx.arc(p.x, p.y, currentSize, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.globalAlpha = alpha;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(p.x, p.y, currentSize * 2, 0, Math.PI * 2);
        ctx.globalAlpha = alpha * 0.25;
        ctx.fill();
      }

      ctx.globalAlpha = 1;

      if (allDone || globalProgress >= 1) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.style.display = "none";
        animFrameRef.current = null;
        return;
      }

      animFrameRef.current = requestAnimationFrame(frame);
    };

    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
    }
    animFrameRef.current = requestAnimationFrame(frame);
  }, []);

  useOnSudoLayoutEvent("trigger_cta_chat", () => {
    animate();
  });

  useEffect(() => {
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, []);

  return createPortal(
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        pointerEvents: "none",
        display: "none",
      }}
    />,
    document.body,
  );
}
